import { Component, ViewEncapsulation, HostListener } from '@angular/core';
import { Router } from '@angular/router';
import { LabelService } from 'src/core/services/label.service';
import { UserRole } from 'src/core/helpers/user-roles';
import { Label, CreateCompanyRequest } from 'src/core/Models/label.model';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { RightsholderBoardService } from 'src/core/services/rightsholder-board.service';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { NotificationService } from '../search-assign/shared/services/notification.service';
import { RightsHolder } from 'src/core/Models/interfaces.model';
import { AuthenticationService } from 'src/core/services/authentication.service';
import { OrganizationService } from 'src/core/services/organization.service';
import { RightsholderItem, RightsholdersResponse } from 'src/core/Models/rightsholders-response';
import { init } from '@sentry/angular-ivy';
import { registerLocaleData } from '@angular/common';
import es from '@angular/common/locales/es';

registerLocaleData(es);
interface RightsholderBoard {
  rightsholderId: number;
  rightsholderName: string;
  parentRightsholderId: number | null;
  parentRightsholderName: string | null;
  labelType: string;
  repertoireAccessLevelId: number;
  boardId: string;
  createdAt: string;
}

interface RightsholderBoardResponse {
  items: RightsholderBoard[];
  pageIndex: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

@Component({
  selector: 'app-label-list',
  templateUrl: './label-list.component.html',
  styleUrls: ['./label-list.component.scss'],
  providers: [LabelService],
  encapsulation: ViewEncapsulation.ShadowDom
})
export class LabelListComponent {

  isAdmin: boolean = false;
  currentUser = JSON.parse(localStorage.getItem('currentUser')!);
  currentUserRole = {
    id: this.currentUser.board.roles[0],
  };
  searchString: string = '';
  jumpToPageNum: number = 1;
  activeSubmenuId: string | null = null;
  selectedLabel: Label | null = null;
  showDeleteLabelDialog: boolean = false;
  organization: string = sessionStorage.getItem('organisation') || 'dk';
  labelForm: FormGroup;
  showAddLabelDialog: boolean = false;
  private searchSubject = new Subject<string>();
  private isHandlingSubmenuClick = false;
  isCreatingLabel: boolean = false;
  userId = this.currentUser.user.id;
  organizationId = (this.organization === 'mpo' || this.organization === 'dk') ? '1' : '2';

  // Rightsholder boards properties
  rightsholderBoards: RightsholderBoard[] = [];
  rightsholderBoardsTotal: number = 0;
  rightsholderBoardsPageIndex: number = 0;
  rightsholderBoardsPageSize: number = 10;
  rightsholderBoardsTotalPages: number = 0;
  hasNextRightsholderBoardsPage: boolean = false;
  hasPreviousRightsholderBoardsPage: boolean = false;
  parentLabels: RightsholderBoard[] = [];

  selectedLabelId: string | null = null;
  selectedLabelName: string | null = null;
  rightsholders: RightsholderItem[] = [];
  isCollectingForItself: boolean = false;
  selectedParentBoardId: string | null = null;
  activeBoardId: any;
  showInactive: boolean = false;

  constructor(
    private labelService: LabelService,
    private rightsholderBoardService: RightsholderBoardService,
    private router: Router,
    private fb: FormBuilder,
    private notificationService: NotificationService,
    public authService: AuthenticationService,
    private organizationService: OrganizationService, 
  ) {
    this.labelForm = this.fb.group({
      name: ['', Validators.required],
      parentLabel: ['', [this.parentLabelValidator.bind(this)]],
      isCollectingForItself: [false]
    });

    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(() => {
      this.rightsholderBoardsPageIndex = 0;
      this.getRightsholders();
    });
  }

  ngOnInit(): void {
    this.isAdmin = UserRole.Superadmin == this.currentUserRole.id;
    this.activeBoardId = this.currentUser.board.id;
    this.initLabelManagement();
  }

  initLabelManagement(): void {

    this.rightsholderBoardService.getRightsholders(
      parseInt(this.organizationId),
      this.rightsholderBoardsPageIndex,
      this.rightsholderBoardsPageSize,
      this.searchString || undefined
    )
      .subscribe({
        next: (res: RightsholdersResponse) => {
          console.debug('Rightsholders:', res);
          this.rightsholders = res.data;
          this.rightsholderBoardsTotalPages = res.total / this.rightsholderBoardsPageSize;
          this.rightsholderBoardsTotal = res.total;
          this.getParentRightsholders();
        },
        error: (err) => {
          console.debug('Failed to fetch rightsholders:', err);
        }
      });
  }

  getUserRightsholderBoards(): void {
    this.rightsholderBoardService.getUserRightsholderBoards(
      this.userId,
      this.organizationId,
      this.rightsholderBoardsPageIndex,
      this.rightsholderBoardsPageSize,
      this.searchString || undefined
    )
      .subscribe({
        next: (res: RightsholderBoardResponse) => {

          this.rightsholderBoards = res.items;
          this.rightsholderBoardsTotal = res.totalCount;
          this.rightsholderBoardsPageIndex = res.pageIndex;
          this.rightsholderBoardsPageSize = res.pageSize;
          this.rightsholderBoardsTotalPages = res.totalPages;
          this.hasNextRightsholderBoardsPage = res.hasNextPage;
          this.hasPreviousRightsholderBoardsPage = res.hasPreviousPage;
        },
        error: (err) => {
          console.error('Failed to fetch user rightsholder boards:', err);
        }
      });
  }


  getParentRightsholders(): void {
    this.rightsholderBoardService.getParentRightsholders(
      this.organizationId
    )
      .subscribe({
        next: (res: any) => {
          console.debug('Parent labels for dropdown:', res);
          this.parentLabels = res.data;
        },
        error: (err) => {
          console.debug('Failed to fetch parent labels:', err);
        }
      });
  }

  get UserRole() {
    return UserRole;
  }

  searchChanged() {
    this.searchSubject.next(this.searchString);
  }

  deleteLabel(labelId: string) {
    this.rightsholderBoardService.deleteRightsholderBoard(labelId)
      .subscribe({
        next: () => {
          this.getRightsholders();
        },
        error: (error) => {
          console.error('Error deleting label:', error);
        }
      });
  }

  switchLabel(boardId: any) {
    if (UserRole.LabelAdmin == this.currentUserRole.id || UserRole.LabelAgent == this.currentUserRole.id || UserRole.LabelUser == this.currentUserRole.id) {
      this.authService.getCurrentBoard(boardId, JSON.parse(
        localStorage.getItem('currentUser')!
      ).authenticated.access_token)
        .subscribe((currentBoard: any) => {
          this.currentUser = {
            ...this.currentUser,
            "board": currentBoard
          };
          localStorage.setItem("currentUser", JSON.stringify(this.currentUser));
          this.authService
            .getCurrentRightsHolderbyBoardId(
              this.currentUser.board.id,
              this.currentUser.authenticated.access_token
            )
            .subscribe((currentRightsHolder: RightsHolder) => {
              this.currentUser = {
                ...this.currentUser,
                rightsHolder: currentRightsHolder
              };
              localStorage.setItem('currentUser', JSON.stringify(this.currentUser));
              this.organizationService.setOrganizationIdInStorage(this.currentUser);
              location.reload();
            });
        });
    }
  }

  previousPage() {
    if (this.rightsholderBoardsPageIndex > 0) {
      this.rightsholderBoardsPageIndex--;
      this.getRightsholders();
    }
  }

  nextPage() {
    if (this.hasNextRightsholderBoardsPage) {
      this.rightsholderBoardsPageIndex++;
      this.getRightsholders();
    }
  }

  goToPage(pageIndex: number) {
    this.rightsholderBoardsPageIndex = pageIndex;
    this.getRightsholders();
  }

  jumpToPage() {
    if (this.jumpToPageNum > 0 && this.jumpToPageNum <= this.rightsholderBoardsTotalPages) {
      this.rightsholderBoardsPageIndex = this.jumpToPageNum - 1;
      this.getRightsholders();
    }
  }

  onPageSizeChange() {
    this.rightsholderBoardsPageIndex = 0;
    this.getRightsholders();
  }

  hasNextPage(): boolean {
    return this.hasNextRightsholderBoardsPage;
  }

  getTotalPages(): number {
    return this.rightsholderBoardsTotalPages;
  }

  @HostListener('document:click')
  closeSubmenu(): void {
    if (this.isHandlingSubmenuClick) {
      this.isHandlingSubmenuClick = false;
      return;
    }

    if (this.activeSubmenuId) {
      this.activeSubmenuId = null;
    }
  }

  toggleSubmenu(labelId: any, event: Event): void {
    event.stopPropagation();
    event.preventDefault();

    this.isHandlingSubmenuClick = true;

    this.activeSubmenuId = (this.activeSubmenuId === labelId) ? null : labelId;
  }

  openDeleteDialog(boardId: string): void {
    const rightsholderBoard = this.rightsholderBoards.find(board => board.boardId === boardId);

    if (rightsholderBoard) {
      this.selectedLabelId = boardId;
      this.selectedLabelName = rightsholderBoard.rightsholderName;
      this.showDeleteLabelDialog = true;
    }
  }

  confirmDelete(): void {
    if (this.selectedLabelId) {
      this.deleteLabel(this.selectedLabelId);
      this.showDeleteLabelDialog = false;
      this.selectedLabelId = null;
      this.selectedLabelName = null;
    }
  }

  cancelDelete(): void {
    this.showDeleteLabelDialog = false;
    this.selectedLabelId = null;
    this.selectedLabelName = null;
  }

  navigateToSettings(labelId: string | undefined, labelName: string | undefined): void {
    if (labelId) {
      this.router.navigate(['/settings', labelId], { queryParams: { tab: 'members' }, state: { labelName: labelName } });
    }
  }

  createLabel() {
    if (this.labelForm.valid && !this.isCreatingLabel) {
      this.isCreatingLabel = true;
      const labelName = this.labelForm.value.name;


      this.labelService.checkLabelExistence(labelName).subscribe({
        next: (existingLabels) => {
          if (existingLabels && existingLabels.length > 0) {
            this.isCreatingLabel = false;
            this.notificationService.showError('A label with this name already exists.');
          } else {
            const selectedParentBoardId = this.parentLabels.find(parent => parent.rightsholderName === this.labelForm.value.parentLabel)?.boardId || null;
            const newLabel: Label = {
              name: labelName,
              title: labelName,
              parentBoardId: selectedParentBoardId || this.currentUser.board.id,
              isCollectingForItself: this.labelForm.value.isCollectingForItself,
              hasImage: false
            };

            this.labelService.addLabel(newLabel, this.currentUser.board.id).subscribe({
              next: (response: any) => {
                const createCompanyRequest: CreateCompanyRequest = {
                  brandName: response.Name,
                  boardId: response.Id,
                  collectDirectly: response.IsCollectingForItself,
                  accessLevelId: 1,
                  IsActive: true,
                  labelType: 'Label', // AKA type of company 
                  companyType: 'Business',
                  memberType: 'Member',
                  collectorId: this.parentLabels.find(parent => parent.boardId === selectedParentBoardId)?.rightsholderName || ""

                };

                this.labelService.createCompany(createCompanyRequest).subscribe({
                  next: (companyResponse: any) => {
                    this.isCreatingLabel = false;
                    this.notificationService.showSuccess('Label added successfully.');

                    if (response && response.Id) {
                      this.router.navigate(['/settings', response.Id], { queryParams: { tab: 'members' } });
                    }
                  },
                  error: (companyError) => {
                    console.error('Error creating company:', companyError);
                    this.isCreatingLabel = false;
                    this.notificationService.showError('We could not create the company. Please check the details and try again.');

                    if (response && response.Id) {
                      this.router.navigate(['/settings', response.Id], { queryParams: { tab: 'members' } });
                    }
                  }
                });

                // sync with rightsholder board in central db
                const newRightsholderBoard: any = {
                  id: response.Id,
                  parentBoardId: selectedParentBoardId,
                  name: response.Name,
                  address: response.Address,
                  membershipNumber: response.MembershipNumber,
                  cvr: response.CVR,
                  isCollectingForItSelf: response.IsCollectingForItSelf,
                  organizationId: this.organizationId || 1
                };
                this.rightsholderBoardService.insertRightsholderBoard(newRightsholderBoard).subscribe(() => { });
              },
              error: (error) => {
                console.error('Error creating label:', error);
                this.isCreatingLabel = false;
                this.notificationService.showError('Failed to create label');
              }
            });
          }
        },
        error: (error) => {
          console.error('Error checking label existence:', error);
          this.isCreatingLabel = false;
          this.notificationService.showError('Failed to check if label exists');
        }
      });
    }
  }

  parentLabelValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null; 
    }
    
    const isValid = this.parentLabels.some(
      parent => parent.rightsholderName === control.value
    );
    
    return isValid ? null : { invalidParentLabel: true };
  }

  openAddLabelModal(): void {
    this.labelForm.reset({
      name: '',
      parentLabel: '',
      isCollectingForItself: false
    });
    this.labelForm.get('name')?.markAsUntouched();
    this.showAddLabelDialog = true;
  }

  closeAddLabelModal(): void {
    this.showAddLabelDialog = false;
  }

  closeModalOnBackgroundClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('modal')) {
      this.showAddLabelDialog = false;
    }
  }

  getPageNumbers(): number[] {
    const totalPages = this.rightsholderBoardsTotalPages;
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      return Array.from({ length: totalPages }, (_, i) => i);
    }

    let startPage = Math.max(0, this.rightsholderBoardsPageIndex - 2);
    let endPage = Math.min(totalPages - 1, startPage + maxVisiblePages - 1);

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(0, endPage - maxVisiblePages + 1);
    }

    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }

  getCurrentActiveLabel(): string {
    try {
      const currentUserItem = localStorage.getItem('currentUser');
      if (currentUserItem && currentUserItem !== 'null') {
        const currentUser = JSON.parse(currentUserItem);
        return currentUser?.board?.name || '';
      }
      return '';
    } catch (error) {
      console.error('Error parsing currentUser from localStorage:', error);
      return '';
    }
  }

  getRightsholders(): void {
    this.rightsholderBoardService.getRightsholders(
      parseInt(this.organizationId),
      this.rightsholderBoardsPageIndex,
      this.rightsholderBoardsPageSize,
      this.searchString || undefined
    )
      .subscribe({
        next: (res: RightsholdersResponse) => {
          console.debug('Rightsholders:', res);
          this.rightsholders = res.data;
            this.rightsholderBoardsTotalPages = Math.ceil(res.total / this.rightsholderBoardsPageSize);

            this.rightsholderBoardsTotal = res.total;
          this.hasNextRightsholderBoardsPage = (this.rightsholderBoardsPageIndex + 1) * this.rightsholderBoardsPageSize < res.total;
        },
        error: (err) => {
          console.debug('Failed to fetch rightsholders:', err);
        }
      });

  }

  toggleCollectingForItself() {
    this.isCollectingForItself = !this.isCollectingForItself;
    this.labelForm.patchValue({ isCollectingForItself: this.isCollectingForItself });
  }

  isValidParentLabel(): boolean {
    const parentLabelValue = this.labelForm.get('parentLabel')?.value;
    if (!parentLabelValue) {
      return false;
    }
    return this.parentLabels.some(parent => parent.rightsholderName === parentLabelValue);
  }
}

